import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import all_products from '../Components/Assets/all_products';

export const ShopContext = createContext(null);

const getDefaultCart = () => ({});

const ShopContextProvider = (props) => {
  const [cartItems, setCartItems] = useState(getDefaultCart());
  const [guestCart, setGuestCart] = useState(getDefaultCart());
  const [isCartInitialized, setIsCartInitialized] = useState(false);

  // Memoized function to get the current storage key
  const getCartStorageKey = useCallback(() => {
    const authToken = localStorage.getItem('auth-token');
    return authToken ? `user-cart-${authToken}` : 'guest-cart';
  }, []);

  // Save cart to localStorage
  const saveCart = useCallback((cart, key) => {
    try {
      localStorage.setItem(key, JSON.stringify(cart));
    } catch (error) {
      console.error('Failed to save cart:', error);
      toast.error('Failed to save your cart. Please try again.');
    }
  }, []);

  // Load cart from localStorage with validation
  const loadCart = useCallback(() => {
    const storageKey = getCartStorageKey();
    try {
      const savedCart = localStorage.getItem(storageKey);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        const validCart = {};
        
        Object.entries(parsedCart).forEach(([compositeId, quantity]) => {
          const baseId = compositeId.split('-')[0];
          if (all_products.some(p => p.id === Number(baseId))) {
            validCart[compositeId] = Math.min(Math.max(quantity, 1), 10);
          }
        });
        
        if (storageKey.includes('guest-cart')) {
          setGuestCart(validCart);
        } else {
          setCartItems(validCart);
        }
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      toast.error('Failed to load your cart. Please try again.');
      if (storageKey.includes('guest-cart')) {
        setGuestCart(getDefaultCart());
      } else {
        setCartItems(getDefaultCart());
      }
    } finally {
      setIsCartInitialized(true);
    }
  }, [getCartStorageKey]);

  // Initialize cart on mount
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Save cart when it changes
  useEffect(() => {
    if (!isCartInitialized) return;
    
    const storageKey = getCartStorageKey();
    const cartToSave = storageKey.includes('guest-cart') ? guestCart : cartItems;
    saveCart(cartToSave, storageKey);
  }, [cartItems, guestCart, getCartStorageKey, saveCart, isCartInitialized]);

  // Toast notification helper
  const showToast = useCallback((message, type = 'info') => {
    toast[type](message, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }, []);

  // Add item to cart
  const addToCart = useCallback((item) => {
    const { id, quantity, selectedSize = '', selectedColor = '' } = item;
    const compositeId = `${id}-${selectedSize}-${selectedColor}`;
    
    const isLoggedIn = !!localStorage.getItem('auth-token');
    const currentCart = isLoggedIn ? cartItems : guestCart;
    const updateCart = isLoggedIn ? setCartItems : setGuestCart;

    const newCart = {...currentCart};
    const currentQuantity = newCart[compositeId] || 0;
    const newQuantity = currentQuantity + quantity;

    if (newQuantity > 10) {
      showToast('You cannot add more than 10 of this item.', 'warning');
      return false;
    }

    newCart[compositeId] = newQuantity;
    updateCart(newCart);
    showToast(`${item.name} added to cart!`, 'success');
    return true;
  }, [cartItems, guestCart, showToast]);

  // Remove item from cart
  const removeFromCart = useCallback((compositeId) => {
    const isLoggedIn = !!localStorage.getItem('auth-token');
    const updateCart = isLoggedIn ? setCartItems : setGuestCart;
    
    updateCart(prev => {
      const newCart = {...prev};
      delete newCart[compositeId];
      return newCart;
    });
    return true;
  }, []);

  // Update item quantity in cart
  const updateCartItemQuantity = useCallback((compositeId, newQuantity) => {
    if (newQuantity < 1 || newQuantity > 10) return;
    
    const isLoggedIn = !!localStorage.getItem('auth-token');
    const updateCart = isLoggedIn ? setCartItems : setGuestCart;
    
    updateCart(prev => ({
      ...prev,
      [compositeId]: newQuantity
    }));
  }, []);

  // Get product details from compositeId
  const getCartProductDetails = useCallback((compositeId) => {
    const [baseId, selectedSize, selectedColor] = compositeId.split('-');
    const product = all_products.find(p => p.id === Number(baseId));
    
    if (!product) return null;
    
    return {
      ...product,
      selectedSize,
      selectedColor,
      newPrice: product.newPrice || 0,
    };
  }, []);

  // Calculate total cart amount
  const getTotalCartAmount = useCallback(() => {
    const isLoggedIn = !!localStorage.getItem('auth-token');
    const items = isLoggedIn ? cartItems : guestCart;
    
    return Object.entries(items).reduce((total, [compositeId, quantity]) => {
      const productDetails = getCartProductDetails(compositeId);
      return total + (productDetails?.newPrice || 0) * quantity;
    }, 0);
  }, [cartItems, guestCart, getCartProductDetails]);

  // Get total number of items in cart
  const getTotalCartItems = useCallback(() => {
    const isLoggedIn = !!localStorage.getItem('auth-token');
    const items = isLoggedIn ? cartItems : guestCart;
    
    return Object.values(items).reduce((sum, quantity) => sum + quantity, 0);
  }, [cartItems, guestCart]);

  // Clear cart
  const clearCart = useCallback(() => {
    const isLoggedIn = !!localStorage.getItem('auth-token');
    const updateCart = isLoggedIn ? setCartItems : setGuestCart;
    updateCart(getDefaultCart());
  }, []);

  // Check authentication before cart actions
  const checkAuthBeforeCartAction = useCallback(() => {
    if (!localStorage.getItem('auth-token')) {
      return { allowed: false, message: 'Please log in to manage your cart.' };
    }
    return { allowed: true };
  }, []);

  // Merge guest cart with user cart after login
  const mergeGuestCart = useCallback(() => {
    if (Object.keys(guestCart).length === 0) return;

    setCartItems(prevUserCart => {
      const mergedCart = {...prevUserCart};
      Object.entries(guestCart).forEach(([compositeId, quantity]) => {
        const newQuantity = (mergedCart[compositeId] || 0) + quantity;
        mergedCart[compositeId] = Math.min(newQuantity, 10);
      });
      return mergedCart;
    });

    localStorage.removeItem('guest-cart');
    setGuestCart({});
    showToast('Your guest cart items have been merged!', 'success');
  }, [guestCart, showToast]);

  // Validate token (placeholder for actual token validation)
  const validateToken = useCallback(() => {
    return !!localStorage.getItem('auth-token');
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    all_products,
    cartItems: localStorage.getItem('auth-token') ? cartItems : guestCart,
    isCartInitialized,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    getTotalCartAmount,
    getTotalCartItems,
    showToast,
    clearCart,
    getCartProductDetails,
    checkAuthBeforeCartAction,
    mergeGuestCart,
    validateToken
  }), [
    cartItems,
    guestCart,
    isCartInitialized,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    getTotalCartAmount,
    getTotalCartItems,
    showToast,
    clearCart,
    getCartProductDetails,
    checkAuthBeforeCartAction,
    mergeGuestCart,
    validateToken
  ]);

  return (
    <ShopContext.Provider value={contextValue}>
      {props.children}
    </ShopContext.Provider>
  );
};

export default ShopContextProvider;